import React, { useState } from 'react';
import useThemeStore from "../../store/themeStore";
import { ThemeProps } from "../../utils/theme";
import FeatureEnquiryModal from './FeatureEnquiryModal';
import ServiceModal from './ServiceModal';
import { 
  FileText, 
  ClipboardList, 
  CreditCard, 
  Search, 
  Building, 
  Zap, 
  ZapOff, 
  Flame, 
  Wrench, 
  Map
} from 'lucide-react';

interface AdditionalProductsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ProductTileProps {
  icon: React.ReactNode;
  title: string;
  onClick: () => void;
}

const ProductTile: React.FC<ProductTileProps> = ({ icon, title, onClick }) => {
  const themes = useThemeStore((state) => state.theme as unknown as ThemeProps);
  
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center justify-center p-6 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors h-32 w-full"
      style={{
        borderColor: themes?.color1 || '#1E5188',
      }}
    >
      <div className="text-3xl mb-2" style={{ color: themes?.color1 || '#1E5188' }}>
        {icon}
      </div>
      <span className="text-sm font-medium text-center text-gray-800">
        {title}
      </span>
    </button>
  );
};

const AdditionalProductsModal: React.FC<AdditionalProductsModalProps> = ({ isOpen, onClose }) => {
  const themes = useThemeStore((state) => state.theme as unknown as ThemeProps);
  
  // Modal state - simplified to match PropertyNavigationBar pattern
  const [selectedService, setSelectedService] = useState<any>(null);
  const [isFeatureModalOpen, setIsFeatureModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState("");

  // Product mapping with IDs and service info (same as FeatureEnquiryModal SERVICE_CONFIG)
  const productServices = {
    "Inspection": {
      productId: "68930d5ae6efc18bc791b671",
      title: "Inspection",
      description: "Professional property inspection services to identify potential issues and ensure your investment is sound. Our detailed reports help you make informed decisions."
    },
    "LRA": {
      productId: "68930d6be6efc18bc791b672", 
      title: "LRA",
      description: "Land Registry searches and applications to secure your property rights and ensure clear title ownership."
    },
    "EICR Inside M25": {
      productId: "68930d7de6efc18bc791b673",
      title: "EICR Inside M25", 
      description: "Electrical Installation Condition Reports for properties within the M25. Essential for landlord compliance and tenant safety."
    },
    "EICR Outside M25": {
      productId: "68930d93e6efc18bc791b674",
      title: "EICR Outside M25",
      description: "Electrical Installation Condition Reports for properties outside the M25. Essential for landlord compliance and tenant safety."
    },
    "Gas CP12": {
      productId: "68930dace6efc18bc791b675",
      title: "Gas CP12",
      description: "Gas safety certificates (CP12) to ensure your rental property meets legal gas safety requirements."
    },
    "Gas CP12 & Boiler Service": {
      productId: "68930dc1e6efc18bc791b676",
      title: "Gas CP12 & Boiler Service", 
      description: "Combined gas safety certificate and boiler servicing for comprehensive heating system maintenance and compliance."
    },
    "Boiler Service": {
      productId: "68930ddbe6efc18bc791b677",
      title: "Boiler Service",
      description: "Professional boiler servicing to ensure efficient operation and extend equipment lifespan."
    },
    "Floorplan": {
      productId: "68930df9e6efc18bc791b678",
      title: "Floorplan",
      description: "Accurate floor plans for marketing materials and property documentation. Essential for listings and valuations."
    },
    "EPC / VESA": {
      productId: "689312b1e6efc18bc791b67c",
      title: "EPC / VESA",
      description: "Energy Performance Certificates and VESA (Vendor and Environmental Site Assessment) reports for property transactions."
    },
    "Inventory": {
      productId: "689312c5e6efc18bc791b67d",
      title: "Inventory",
      description: "Detailed property inventories to document condition and contents at the start of tenancies."
    },
    "Checkout": {
      productId: "689312d7e6efc18bc791b67e",
      title: "Checkout",
      description: "End-of-tenancy checkout services to assess property condition and document any changes or damages."
    },
    // Bundle Services
    "EPC / VESA / Floorplans": {
      productId: "68931707e6efc18bc791b679",
      title: "EPC / VESA / Floorplans Bundle",
      description: "Combined package of Energy Performance Certificate, VESA report, and accurate floor plans at a discounted rate."
    },
    "Inventory & Check In": {
      productId: "68931722e6efc18bc791b67a", 
      title: "Inventory & Check In Bundle",
      description: "Complete move-in documentation package including detailed inventory and check-in inspection services."
    },
    "EPC / VESA & LRA": {
      productId: "6893173ae6efc18bc791b67b",
      title: "EPC / VESA & LRA Bundle", 
      description: "Essential property compliance bundle combining energy certificates, environmental assessment, and Land Registry services."
    }
  };

  // Product tiles data
  const products = [
    { icon: <Search />, title: "Inspection" },
    { icon: <FileText />, title: "LRA" },
    { icon: <Zap />, title: "EICR Inside M25" },
    { icon: <ZapOff />, title: "EICR Outside M25" },
    { icon: <Flame />, title: "Gas CP12" },
    { icon: <Wrench />, title: "Gas CP12 & Boiler Service" },
    { icon: <Wrench />, title: "Boiler Service" },
    { icon: <Map />, title: "Floorplan" },
    { icon: <FileText />, title: "EPC / VESA" },
    { icon: <ClipboardList />, title: "Inventory" },
    { icon: <CreditCard />, title: "Checkout" }
  ];

  const bundles = [
    { icon: <FileText />, title: "EPC / VESA / Floorplans" },
    { icon: <ClipboardList />, title: "Inventory & Check In" },
    { icon: <Building />, title: "EPC / VESA & LRA" }
  ];

  // Handle product click - same pattern as PropertyNavigationBar
  const handleProductClick = (productTitle: string) => {
    const serviceInfo = productServices[productTitle as keyof typeof productServices];
    if (!serviceInfo) {
      console.error(`No service info found for: ${productTitle}`);
      return;
    }

    console.log(`🎯 Additional Product selected: ${productTitle}`);
    setSelectedService(serviceInfo);
  };

  // Handle enquire - same pattern as PropertyNavigationBar
  const handleEnquire = (productId: string) => {
    setSelectedProductId(productId);
    setIsFeatureModalOpen(true);
    setSelectedService(null); // Close service modal
  };

  // Utility function to check for white or near-white shades
  const isWhiteOrShade = (color: string): boolean => {
    if (!color) return false;
    if (color?.toLowerCase() === "white" || color === "rgba(255,255,255,1)") return true;
    if (/^#fff[a-f0-9]{0,3}$/i.test(color)) return true;
    const rgba = color?.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]+)?\)/);
    if (rgba) {
      const [, r, g, b] = rgba.map(Number);
      return r >= 240 && g >= 240 && b >= 240;
    }
    return false;
  };

  if (!isOpen) return null;

  return (
    <>
      <div 
        className="fixed inset-0 flex items-center justify-center z-[1000]"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.25)' }}
        onClick={onClose}
      >
        <div 
          className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div 
            className="flex justify-between items-center px-6 py-8 rounded-t-lg"  // Changed p-6 to px-6 py-8
            style={{ 
              backgroundColor: themes?.color1 || '#1E5188',
              color: isWhiteOrShade(themes?.color1) ? "#000000" : "#ffffff"
            }}
          >
            <h2 className="text-2xl font-bold">Additional Products & Services</h2>
            <button 
              onClick={onClose}
              className="text-2xl font-bold hover:scale-110 transition-transform"
              style={{ 
                color: isWhiteOrShade(themes?.color1) ? "#000000" : "#ffffff"
              }}
            >
              &times;
            </button>
          </div>

          {/* Content */}
          <div className="p-6" style={{
            backgroundColor: 'white',
         
          }}>
            <p className="text-gray-600 mb-8 text-center">
              Explore our comprehensive range of additional services to support your property investment journey.
            </p>

            {/* Individual Products */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold mb-4 text-gray-800">Individual Services</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {products.map((product, index) => (
                  <ProductTile
                    key={index}
                    icon={product.icon}
                    title={product.title}
                    onClick={() => handleProductClick(product.title)}
                  />
                ))}
              </div>
            </div>

            {/* Bundle Services */}
            <div>
              <h3 className="text-xl font-semibold mb-4 text-gray-800">Bundle Services</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {bundles.map((bundle, index) => (
                  <ProductTile
                    key={index}
                    icon={bundle.icon}
                    title={bundle.title}
                    onClick={() => handleProductClick(bundle.title)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Service Modal - same as PropertyNavigationBar */}
      {selectedService && (
        <ServiceModal
          isOpen={!!selectedService}
          onClose={() => setSelectedService(null)}
          content={selectedService}
          onEnquire={handleEnquire}
        />
      )}

      {/* Feature Enquiry Modal - same as PropertyNavigationBar */}
      {isFeatureModalOpen && (
        <FeatureEnquiryModal
          isOpen={isFeatureModalOpen}
          onClose={() => setIsFeatureModalOpen(false)}
          productId={selectedProductId}
        />
      )}
    </>
  );
};

export default AdditionalProductsModal;